import React, { useEffect, useState } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  IconButton, CircularProgress, Snackbar, Alert, Dialog, Button, AlertColor, Box, Typography,
  DialogTitle,
  DialogActions,
  DialogContent,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { hasPermission } from "src/components/Permissions/hasPermission";

import AddModulePermissions from "./AddModulePermissions";
import { deleteEntry, get } from 'src/services/httpClient.service';
import { authenticationService } from "src/services/authentication.service";
import FormDialog from "src/components/DialogBox";

const API_URL = process.env.REACT_APP_BASE_API_URL;

interface ModulePermission {
  id: string;
  name: string;
  permissions: string;
  description: string;
  createdDate: string;
  updatedDate: string;
  vendorId: string;
  prefix: string;
  sequence: number;
}

const ModulePermissions = () => {
  const {vendorId, franchiseId} = authenticationService.getVendorFranchiseIds();
  
  const [vendorsList, setVendors] = useState([]);
  const [vendorMap, setVendorMap] = useState({});
  const [rows, setRows] = useState<ModulePermission[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPermission, setSelectedPermission] = useState<ModulePermission | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openUpdateDialog, setOpenUpdateDialog] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: AlertColor;
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    fetchPermissionsData();
  }, []);

  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const response = await get(`${API_URL}/api/vendors`);
  
        // Save array
        setVendors(response);

        // Create a map using vendor id
        const map = response.reduce((acc, vendor) => {
          acc[vendor.vendorId] = vendor;
          return acc;
        }, {});
        setVendorMap(map);

      } catch (err) {
        console.error('Error fetching vendors:', err);
        setError('Failed to load vendors.');
      }
    };
  
    fetchVendors();
  }, [API_URL]);

  const fetchPermissionsData = async () => {
    try {
      setLoading(true);

      const response = await get(`${API_URL}/api/applicationModules/vendor/${vendorId}/findAll`);
      const responseData = response?.data;
      if (!responseData || !Array.isArray(responseData)) {
        setRows([]); // fallback to empty array
        setError("No module permissions found.");
        return;
      }

      const rowsWithPermissions = responseData.map(module => {
        const permissionNames = module.module_permissions?.map((p: any) => p.name) || [];
        return {
          ...module,
          permissions: permissionNames.join(", ")
        };
      });
      setRows(rowsWithPermissions);
      setError("");
    } catch (err) {
      console.error("Error fetching module permissions:", err);
      setError("Failed to load module permissions. Please try again.");
      setSnackbar({
        open: true,
        message: "Failed to load module permissions.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (permission: ModulePermission) => {
    setSelectedPermission(permission);
    setOpenUpdateDialog(true);
  };

  const handleDeleteClick = (permission: ModulePermission) => {
    setSelectedPermission(permission);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleteDialogOpen(false);
    if (!selectedPermission) return;

    try {
      await deleteEntry(`${API_URL}/api/permissions?permissionId=${selectedPermission.id}`);
      setSnackbar({
        open: true,
        message: "Module permission deleted successfully!",
        severity: "success",
      });
      fetchPermissionsData();
    } catch (err) {
      console.error("Error deleting module permission:", err);
      setSnackbar({
        open: true,
        message: "Failed to delete module permission.",
        severity: "error",
      });
    }
  };

  const onClose = () => {
    setOpenDialog(false);
    setOpenUpdateDialog(false);
    setSelectedPermission(null);
  };

  const handleAddPermissionSubmit = async () => {
    fetchPermissionsData();
  };

  const handleClick = () => {
    setOpenDialog(true);
  };

  return (
    <Box p={3}>
      <Box
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 2,
        }}
      >
        <Typography variant="h3" component="h1" gutterBottom>
          Module Permissions Management
        </Typography>
        {hasPermission(["OWNER", "Admin"]) && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleClick}
          >
            Add Permission Modules
          </Button>
        )}
      </Box>

      <TableContainer component={Paper} style={{ marginTop: 20 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" padding={4}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box color="red" textAlign="center" padding={4}>
            {error}
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Module Name</strong></TableCell>
                <TableCell><strong>Description</strong></TableCell>
                <TableCell><strong>Permissions</strong></TableCell>
                <TableCell><strong>Vendor</strong></TableCell>
                <TableCell><strong>Created Date</strong></TableCell>
                {/* <TableCell><strong>Actions</strong></TableCell> */}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length > 0 ? (
                rows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>{row.description}</TableCell>
                    <TableCell>{row.permissions}</TableCell>
                    <TableCell>{vendorMap[row.vendorId]?.vendorName || "N/A"}</TableCell>
                    <TableCell>
                      {row.createdDate
                        ? new Date(row.createdDate).toLocaleDateString()
                        : "N/A"}
                    </TableCell>
                    <TableCell>
                      {hasPermission(["OWNER", "Admin"]) && (
                        <>
                          {/* <IconButton
                            color="primary"
                            onClick={() => handleEditClick(row)}
                            size="small"
                          >
                            <EditIcon sx={{ color: "blue" }} />
                          </IconButton> */}
                          {/* <IconButton
                            color="secondary"
                            onClick={() => handleDeleteClick(row)}
                            size="small"
                          >
                            <DeleteIcon sx={{ color: "red" }} />
                          </IconButton> */}
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No permissions found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* Add Permission Dialog */}
      {openDialog && (
        <Dialog open={openDialog} onClose={onClose} maxWidth="md" fullWidth>
          <DialogTitle>Add Module Permissions</DialogTitle>
          <DialogContent>
            <AddModulePermissions
              handleClose={onClose}
              onSubmit={handleAddPermissionSubmit}
              fetchPermissionsData={fetchPermissionsData}
            />
          </DialogContent>
        </Dialog>
      )}
      {/* Edit Permission Dialog */}
      {openUpdateDialog && (
        <FormDialog
          open={openUpdateDialog}
          onClose={onClose}
          title="Update Permission"
          component="editmodulepermission"
          onSubmit={handleAddPermissionSubmit}
          fetchModulesData={fetchPermissionsData}
          module={selectedPermission}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          Are you sure you want to delete the permission "{selectedPermission?.permissions}"?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for Success/Error Messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ModulePermissions;